import * as Portal from "@radix-ui/react-portal";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  HelpCircle,
  Loader,
  PauseCircle,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { BackgroundJob } from "@/apis/gql/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { TranslationKey } from "@/localisation/types";
import { useBackgroundJobs } from "@/providers/background-jobs";

export function BackgroundJobStatusTracker() {
  const { backgroundJobs: jobs, clearBackgroundJobs } = useBackgroundJobs({
    pick: [],
  });

  const { t } = useTranslation();

  const [value, setValue] = useState<string>("");

  const getStatusIcon = (status: BackgroundJob["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="text-green-500" size={16} />;
      case "failed":
        return <XCircle className="text-red-500" size={16} />;
      case "active":
      case "waiting-children":
        return <Loader className="text-gray-500 animate-spin" size={16} />;
      case "waiting":
      case "delayed":
        return <Clock className="text-yellow-500" size={16} />;
      case "paused":
        return <PauseCircle className="text-orange-500" size={16} />;
      case "stuck":
        return <AlertCircle className="text-red-500" size={16} />;
      default:
        return <HelpCircle className="text-gray-500" size={16} />;
    }
  };

  useEffect(() => {
    if (jobs.length > 0) {
      setValue("jobs");
    }
  }, [jobs]);

  const handleOnValueChange = (value: string) => {
    setValue(value);
  };

  const handleClose = () => {
    clearBackgroundJobs();
    setValue("");
  };

  if (jobs.length === 0) {
    return null;
  }

  return (
    <Portal.Root container={document.getElementById("status-cards")}>
      <Accordion
        type="single"
        collapsible
        value={value}
        onValueChange={handleOnValueChange}
        disabled={jobs.length === 0}
      >
        <AccordionItem value="jobs" className="border-none">
          <Card
            className="shadow-lg p-4"
            style={{
              width: "450px",
            }}
          >
            <div className="flex flex-1 items-center justify-between">
              <div className="flex-1">
                <AccordionTrigger className="p-0 w-full [&[data-state=open]]:no-underline hover:no-underline">
                  <CardHeader className="p-1 w-full">
                    <CardTitle className="w-full text-start">
                      {t("background_jobs")}
                    </CardTitle>
                  </CardHeader>
                </AccordionTrigger>
              </div>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: no comments */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="hover:bg-gray-100 p-1 rounded-full ml-2"
              >
                <X size={16} />
              </div>
            </div>
            <AccordionContent className="pb-0">
              <CardContent className="p-2 space-y-4">
                {jobs.map((job) => (
                  <div key={job.jobId} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center justify-center space-x-2">
                        {getStatusIcon(job.status)}
                        <span className="font-medium">
                          {
                            t(
                              `background_job_name.${job.jobName}` as TranslationKey,
                            ) as string
                          }
                        </span>
                        {job.meta?.label && (
                          <span className="text-xs text-gray-500">
                            {t(job.meta.label as TranslationKey) as string}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500 capitalize">
                        {
                          t(
                            `background_job_status.${job.status}` as TranslationKey,
                          ) as string
                        }
                      </span>
                    </div>
                    <Progress value={job.progress} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>
      </Accordion>
    </Portal.Root>
  );
}
